import type { Permission } from '@natashu/shared';
import type { GuideKey } from '../features/guide/types.ts';
import {
  Activity,
  Bell,
  Bot,
  CalendarClock,
  CalendarDays,
  ChartPie,
  Images,
  ClipboardList,
  ChartColumn,
  ContactRound,
  FolderTree,
  History,
  House,
  Import,
  Inbox,
  LayoutDashboard,
  ListChecks,
  ListFilter,
  MapPin,
 
  Megaphone,
  MessageSquareText,
  MessagesSquare,
  Plug,
  RotateCcw,
  Send,
  BotMessageSquare,
  Settings,
  SlidersHorizontal,
  ReceiptText,
  Trophy,
  Link2,
  UserCog,
  UsersRound,
  type LucideIcon,
} from 'lucide-react';

export type NavKey =
  | 'dashboard'
  | 'inbox'
  | 'savedReplies'
  | 'registrations'
  | 'registrationsCalendar'
  | 'registrationsOverview'
  | 'registrationsKits'
  | 'campaigns'
  | 'schedule'
  | 'deliveries'
  | 'retryCenter'
  | 'contacts'
  | 'teams'
  | 'events'
  | 'payments'
  | 'folders'
  | 'related'
  | 'segments'
  | 'ai'
  | 'notifications'
  | 'reports'
  | 'connections'
  | 'imports'
  | 'slips'
  | 'telegram'
  | 'users'
  | 'settings'
  | 'audit'
  | 'systemHealth'
  | 'venue';

export interface NavItem {
  key: NavKey;
  to: string;
  /** Tab label inside its section: short, and says what the page is for. */
  label: string;
  description: string;
  icon: LucideIcon;
  /** Visible when the user holds any of these. The API enforces the real rules. */
  anyOf: readonly Permission[];
  /** Route stays (open by URL / from other pages) but the page is left out of the menu, tabs and search. */
  hidden?: boolean;
}

export interface NavGroup {
  key: string;
  label: string;
  items: readonly NavItem[];
}

export type NavSectionKey = 'home' | 'chat' | 'contacts' | 'registrations' | 'customers' | 'related' | 'broadcast' | 'bot' | 'competition' | 'settings';

/** Data attached to routes (`handle`) — read by the shell for titles, tabs and permission gates. */
export interface RouteHandle {
  title: string;
  group?: string;
  /** Sidebar section of the page; its sibling pages are shown as tabs. */
  section?: NavSectionKey;
  anyOf?: readonly Permission[];
  /** Which “คู่มือ” the topbar guide button opens on this page. */
  guideKey?: GuideKey;
}

/**
 * One sidebar entry per job the owner does — "เห็นปุ๊บรู้เลยว่าเมนูนี้ใช้ทำอะไร".
 * Every former menu keeps its URL and is reached as a tab of its section (docs/decisions/0002-task-based-navigation.md).
 */
export interface NavSection {
  key: NavSectionKey;
  label: string;
  /** Bottom tab bar on phones. */
  shortLabel: string;
  description: string;
  icon: LucideIcon;
  items: readonly NavItem[];
  /** Hidden unless the workspace switched the optional module on (ตั้งค่าทั่วไป → งานแข่งขัน). */
  feature?: 'competition';
  /** Shown apart from daily work at the bottom of the sidebar. */
  footer?: boolean;
}

export const NAV_SECTIONS: readonly NavSection[] = [
  {
    key: 'home',
    label: 'หน้าแรก',
    shortLabel: 'หน้าแรก',
    description: 'สรุปงานวันนี้ และรายงาน',
    icon: House,
    items: [
      { key: 'dashboard', to: '/', label: 'ภาพรวมวันนี้', description: 'แชทที่รอตอบ ข้อความที่ส่ง และสถานะระบบ', icon: LayoutDashboard, anyOf: ['reports.read'] },
      { key: 'reports', to: '/reports', label: 'รายงาน', description: 'สถิติแชท การส่งข้อความ และการตอบของทีม', icon: ChartColumn, anyOf: ['reports.read'] },
    ],
  },
  {
    key: 'chat',
    label: 'แชทลูกค้า',
    shortLabel: 'แชท',
    description: 'อ่านและตอบแชทจากทุกเพจ ทุกแพลตฟอร์มในที่เดียว',
    icon: MessagesSquare,
    items: [
      { key: 'inbox', to: '/inbox', label: 'แชทลูกค้า', description: 'อ่านและตอบแชทจากทุกเพจ ทุกแพลตฟอร์ม', icon: Inbox, anyOf: ['conversations.read_all', 'conversations.read_assigned'] },
    ],
  },
  {
    key: 'contacts',
    label: 'รายชื่อลูกค้า',
    shortLabel: 'ลูกค้า',
    description: 'ข้อมูลลูกค้าแต่ละคน ช่องทางที่ทักมา ใบสมัคร และประวัติ',
    icon: ContactRound,
    items: [
      { key: 'contacts', to: '/contacts', label: 'รายชื่อลูกค้า', description: 'ข้อมูลลูกค้าแต่ละคน ช่องทางที่ทักมา ใบสมัคร และประวัติ', icon: ContactRound, anyOf: ['crm.read'] },
    ],
  },
  {
    key: 'registrations',
    label: 'ใบสมัคร',
    shortLabel: 'ใบสมัคร',
    description: 'ใบสมัครทุกทีม ปฏิทินวันแข่ง และสรุปว่าสมัครเข้ามาวันไหนมากสุด',
    icon: ClipboardList,
    items: [
      { key: 'registrations', to: '/registrations', label: 'รายการใบสมัคร', description: 'ค้นหา แก้ไข คอนเฟิร์ม ยกเลิก และดูประวัติใบสมัคร', icon: ClipboardList, anyOf: ['crm.read'] },
      { key: 'registrationsCalendar', to: '/registrations/calendar', label: 'ปฏิทินใบสมัคร', description: 'แต่ละวันมีกี่ทีม รุ่นไหนบ้าง', icon: CalendarDays, anyOf: ['crm.read'] },
      { key: 'registrationsOverview', to: '/registrations/overview', label: 'ภาพรวมใบสมัคร', description: 'สมัครวันไหน เดือนไหนมากสุด–น้อยสุด แยกรุ่น รายการ จังหวัด', icon: ChartPie, anyOf: ['crm.read'] },
      // v1.93.0 (owner): what the team links hold per match day, and "ล้างรูป" to give the space back.
      { key: 'registrationsKits', to: '/registrations/team-kits', label: 'ข้อมูลทีมจากลิงก์', description: 'โลโก้และรูปผู้เล่นที่ทีมใส่ผ่านลิงก์ ดูขนาด และล้างรูปทีละรายการ', icon: Images, anyOf: ['crm.read'] },
    ],
  },
  {
    key: 'customers',
    // v1.26.0: รายชื่อลูกค้า moved to its own menu (owner); this menu keeps the categories.
    label: 'หมวดหมู่ลูกค้า',
    shortLabel: 'หมวดหมู่',
    description: 'จัดลูกค้าเข้าหมวดหมู่ เช่น รุ่น U10 U12',
    icon: FolderTree,
    items: [
      { key: 'folders', to: '/folders', label: 'หมวดหมู่ลูกค้า', description: 'จัดลูกค้าเข้าหมวดหมู่ เช่น รุ่น U10 U12', icon: FolderTree, anyOf: ['folders.manage', 'crm.read'] },
      // Left the menu in v1.16.2 — the owner found it confusing. Still opens by URL; the advanced send wizard can pick saved groups.
      { key: 'segments', to: '/segments', label: 'กลุ่มตามเงื่อนไข', description: 'ตัวกรองลูกค้าที่บันทึกไว้ (ขั้นสูง)', icon: ListFilter, anyOf: ['segments.manage', 'campaigns.read'], hidden: true },
    ],
  },
  {
    // v1.32.0 (owner): one customer's accounts across platforms/pages — search, link, detach and their history.
    key: 'related',
    label: 'บัญชีที่มีความสัมพันธ์',
    shortLabel: 'บัญชีสัมพันธ์',
    description: 'ลูกค้าคนเดียวกันหลายแพลตฟอร์ม ผูก/แยกบัญชี และประวัติ',
    icon: Link2,
    items: [
      { key: 'related', to: '/related', label: 'บัญชีที่มีความสัมพันธ์', description: 'ลูกค้าคนเดียวกันหลายแพลตฟอร์ม ผูก/แยกบัญชี และประวัติ', icon: Link2, anyOf: ['crm.read'] },
    ],
  },
  {
    key: 'broadcast',
    label: 'ส่งข้อความถึงหมวดหมู่',
    shortLabel: 'ส่งข้อความ',
    description: 'ส่งข้อความถึงลูกค้าทั้งหมวดหมู่ ส่งทันทีหรือตั้งเวลา',
    icon: Send,
    items: [
      { key: 'campaigns', to: '/campaigns', label: 'รายการส่งข้อความ', description: 'สร้างการส่งข้อความถึงหมวดหมู่ และดูผล', icon: Megaphone, anyOf: ['campaigns.read'] },
      { key: 'schedule', to: '/schedule', label: 'ตั้งเวลาไว้', description: 'ข้อความที่ตั้งเวลาส่งไว้ทั้งหมด', icon: CalendarClock, anyOf: ['campaigns.read'] },
      { key: 'deliveries', to: '/deliveries', label: 'ผลการส่งรายคน', description: 'ส่งถึงใครแล้ว ใครอ่านแล้ว ใครส่งไม่ได้', icon: ListChecks, anyOf: ['campaigns.read'] },
      { key: 'retryCenter', to: '/retry-center', label: 'ส่งไม่สำเร็จ', description: 'ข้อความที่ส่งไม่สำเร็จ และส่งใหม่อย่างปลอดภัย', icon: RotateCcw, anyOf: ['deliveries.retry'] },
    ],
  },
  {
    key: 'bot',
    label: 'บอทตอบอัตโนมัติ',
    shortLabel: 'บอท',
    description: 'ตั้งคำตอบไว้ล่วงหน้า บอทตอบให้เองหรือเตรียมร่างให้กดยืนยัน',
    icon: Bot,
    items: [{ key: 'ai', to: '/bot', label: 'บอทตอบอัตโนมัติ', description: 'คำถาม–คำตอบ · ตอบตามหมวดหมู่ · ทดสอบบอท', icon: Bot, anyOf: ['knowledge.manage'] }],
  },
  {
    key: 'competition',
    label: 'งานแข่งขัน',
    shortLabel: 'แข่งขัน',
    description: 'รายการแข่ง ใบสมัคร ทีม และตรวจสลิป',
    icon: Trophy,
    feature: 'competition',
    items: [
      { key: 'events', to: '/events', label: 'รายการแข่ง & ใบสมัคร', description: 'รายการแข่ง รุ่น และใบสมัครของแต่ละทีม', icon: Trophy, anyOf: ['crm.read'] },
      { key: 'teams', to: '/teams', label: 'ทีมฟุตบอล', description: 'ทีมที่สมัครแข่งและผู้ประสานงาน', icon: UsersRound, anyOf: ['crm.read'] },
      { key: 'payments', to: '/payments', label: 'ตรวจสลิป', description: 'ตรวจสลิปและยืนยันยอดเงิน', icon: ReceiptText, anyOf: ['payments.read'] },
    ],
  },
  {
    key: 'settings',
    label: 'ตั้งค่า',
    shortLabel: 'ตั้งค่า',
    description: 'เชื่อมเพจ/LINE ทีมงาน การแจ้งเตือน และระบบ',
    icon: Settings,
    footer: true,
    items: [
      { key: 'connections', to: '/connections', label: 'เชื่อมต่อเพจ & LINE', description: 'เชื่อม Facebook เพจ, Instagram, LINE OA, Telegram', icon: Plug, anyOf: ['connections.manage', 'reports.read'] },
      { key: 'users', to: '/users', label: 'ทีมงาน & สิทธิ์', description: 'บัญชีทีมงาน และใครทำอะไรได้บ้าง', icon: UserCog, anyOf: ['users.manage'] },
      { key: 'notifications', to: '/notifications', label: 'แจ้งเตือนทีมงาน', description: 'ส่งแจ้งเตือนเข้า LINE/Telegram ของทีมเมื่อมีเรื่องสำคัญ', icon: Bell, anyOf: ['notifications.manage'] },
      { key: 'settings', to: '/settings', label: 'ตั้งค่าทั่วไป', description: 'เวลาทำการ ค่าเริ่มต้น และเปิด/ปิดงานแข่งขัน', icon: SlidersHorizontal, anyOf: ['settings.manage'] },
      // v1.68.0 (owner): arranging the saved replies needs room and a number on every row — not a popover.
      { key: 'savedReplies', to: '/saved-replies', label: 'การตอบกลับที่บันทึกไว้', description: 'ข้อความที่ตอบบ่อย เรียงลำดับเองได้ว่าอันไหนอยู่บนสุด', icon: MessageSquareText, anyOf: ['conversations.reply'] },
      // v1.92.0 (owner): its own tab, next to ตรวจสลิป — not inside the registration pages.
      { key: 'telegram', to: '/telegram', label: 'เทเลแกรม', description: 'บอทเทเลแกรม: แจ้งเตือนใบสมัคร และเมนูปุ่มดูรายชื่อทีม', icon: BotMessageSquare, anyOf: ['settings.manage'] },
      { key: 'slips', to: '/slips', label: 'ตรวจสลิป', description: 'ตรวจสลิปโอนเงินอัตโนมัติ บัญชีรับเงิน และประวัติการตรวจ', icon: ReceiptText, anyOf: ['crm.read'] },
      { key: 'imports', to: '/imports', label: 'นำเข้าข้อมูล', description: 'นำเข้ารายชื่อจากไฟล์ และซิงก์จากระบบสมัครแข่ง', icon: Import, anyOf: ['imports.manage', 'sync.manage'] },
      { key: 'audit', to: '/audit', label: 'ประวัติการแก้ไข', description: 'ใครแก้อะไร เมื่อไหร่', icon: History, anyOf: ['audit.read'] },
      { key: 'systemHealth', to: '/system-health', label: 'สุขภาพระบบ', description: 'เซิร์ฟเวอร์ พื้นที่เก็บข้อมูล และความเสี่ยง', icon: Activity, anyOf: ['system.manage'] },
      // v1.99.0 (owner): where the field is — sent to customers as {แผนที่สนาม}.
      { key: 'venue', to: '/venue', label: 'ตั้งค่าตำแหน่ง', description: 'ตำแหน่งสนาม (ละติจูด/ลองจิจูด) และลิงก์แผนที่ให้ลูกค้า', icon: MapPin, anyOf: ['settings.manage'] },
    ],
  },
];

/** Section view as label + items, for code that lists every page (command palette, permission gate). */
export const NAV_GROUPS: readonly NavGroup[] = NAV_SECTIONS.map((s) => ({ key: s.key, label: s.label, items: s.items }));

export type NavItemWithGroup = NavItem & { group: string; section: NavSectionKey };

export const NAV_ITEMS: readonly NavItemWithGroup[] = NAV_SECTIONS.flatMap((s) => s.items.map((i) => ({ ...i, group: s.label, section: s.key })));

export function navItem(key: NavKey): NavItemWithGroup {
  const item = NAV_ITEMS.find((i) => i.key === key);
  if (!item) throw new Error(`Unknown nav key: ${key}`);
  return item;
}

export function navSection(key: NavSectionKey): NavSection {
  const section = NAV_SECTIONS.find((s) => s.key === key);
  if (!section) throw new Error(`Unknown nav section: ${key}`);
  return section;
}

/** Phone bottom tab bar (DESIGN §15), followed by "เพิ่มเติม". */
export const MOBILE_SECTION_KEYS: readonly NavSectionKey[] = ['chat', 'contacts', 'registrations', 'home'];
